/* THE CONTACT SHEET — one HTML file that shows what is in the ZIP.

   A wall export is a folder of files named R1C1, R1C2, R2C1… and a README
   naming which is which. That is correct and it is not a picture: nobody can
   look at six MP4s in Explorer and tell whether the piece reads across the
   seams, and the person who opens the folder is often not the person who made
   it — an installer, months later, checking they have the right set.

   So the ZIP carries a page. The wall is laid out at the top with every clip
   PLAYING IN ITS OWN POSITION, bezel gaps and all, so the piece can be read
   across the seams the way it will be on site. Underneath, the same clips as a
   two-column list with their filenames, which is the half an installer needs.

   It replaced a scale drawing (ui/plan-view.js's, serialised). The drawing
   said what shape the wall is; six clips playing in formation say the same
   thing and also show the thing itself, which is what somebody opening this
   folder came for. The dimensions it carried are kept as a line of text.

   SELF-CONTAINED, with one deliberate exception. The CSS is inline, the logo
   is a data URI, there is no script and nothing is fetched from the network —
   open it with no internet and it is whole. The VIDEOS are referenced as
   siblings rather than embedded, because base64 inflates bytes by a third and
   a six-screen wall would double the size of the ZIP to say what the files
   next to it already say. Unzip and open; that is the contract. */

import { freeBounds } from '../scene/panels.js';
import { S } from '../state/store.js';
import { projectSlug } from './filename.js';

const SITE = 'www.illusio3d.com';

/* The builder's own note, for the builder's own walls. */
const NOTE = [
  'Each clip was rendered for one viewer standing in one place.',
  'Swapping two screens, or mirroring any of them, breaks the illusion rather',
  'than reversing it. The clips play from the files beside this page, so keep',
  'them together.'
].join('\n  ');

const esc = s => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const m2 = n => (Math.round(n * 100) / 100).toFixed(2);

/* The mark, inlined. Fetched once per session and kept — a wall export writes
   one sheet, but an export of the stills and then the clips writes two. */
let logoPromise = null;
function logoDataUrl() {
  if (!logoPromise) {
    logoPromise = fetch('/Illusio3D_Icon.png')
      .then(r => (r.ok ? r.blob() : Promise.reject(new Error('logo ' + r.status))))
      .then(b => new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(rd.result);
        rd.onerror = () => rej(new Error('logo could not be read'));
        rd.readAsDataURL(b);
      }))
      /* A missing mark must never cost somebody their export. The sheet simply
         goes out without it. */
      .catch(() => '');
  }
  return logoPromise;
}

/**
 * Where each panel sits on the wall, as percentages of the wall's own box.
 *
 * Percentages rather than pixels, so the block scales to whatever width the
 * page is read at and the gaps scale with it — a bezel that stayed 2px while
 * the wall grew would stop being the gap it is describing.
 *
 * @returns {{cells:Array<{left:number,top:number,width:number,height:number}>,
 *            aspect:number, metres:{w:number,h:number}}}
 */
export function wallCells(plan) {
  const a = S.screenArray;
  if (a && a.mode === 'free') {
    /* freeBounds gives the same box panelDescs() placed every screen inside,
       so this cannot disagree with what was actually rendered — and its x/y
       are already top-left-and-down, which is exactly what a CSS percentage
       needs, so each screen's own numbers ARE its cell with no conversion. */
    const b = freeBounds(a.screens);
    return {
      aspect: b.w / b.h,
      metres: { w: b.w, h: b.h },
      cells: a.screens.map(s => ({
        left: (s.x - b.x0) / b.w * 100,
        top: (s.y - b.y0) / b.h * 100,
        width: s.w / b.w * 100,
        height: s.h / b.h * 100
      }))
    };
  }
  if (a) {
    const W = a.cols * a.tile.w + (a.cols - 1) * a.gap;
    const H = a.rows * a.tile.h + (a.rows - 1) * a.gap;
    return {
      aspect: W / H,
      metres: { w: W, h: H },
      cells: plan.map(p => ({
        left: (p.desc.col * (a.tile.w + a.gap)) / W * 100,
        top: (p.desc.row * (a.tile.h + a.gap)) / H * 100,
        width: a.tile.w / W * 100,
        height: a.tile.h / H * 100
      }))
    };
  }
  /* A corner screen's two panels, or a single screen's one. Laid out side by
     side by their real widths, sharing full height — which is what a corner is
     once it has been unfolded, and the order panels come in is left then
     right. */
  const widths = plan.map(p => p.w || S.screen.w);
  const W = widths.reduce((x, y) => x + y, 0);
  const H = S.screen.h;
  let x = 0;
  return {
    aspect: W / H,
    metres: { w: W, h: H },
    cells: plan.map((p, i) => {
      const cell = { left: x / W * 100, top: 0, width: widths[i] / W * 100, height: 100 };
      x += widths[i];
      return cell;
    })
  };
}

/** What this installation is, in one line. */
function summary(plan, wall) {
  const a = S.screenArray;
  if (a && a.mode === 'free')
    return `${plan.length} screens placed freely · ${m2(wall.metres.w)} × ${m2(wall.metres.h)} m overall`;
  if (a) {
    const gap = a.gap > 0 ? `${m2(a.gap)} m gaps` : 'edge to edge';
    return `${a.cols} across × ${a.rows} down · ${plan.length} screens of `
         + `${a.res.w} × ${a.res.h} px · ${gap} · ${m2(wall.metres.w)} × ${m2(wall.metres.h)} m overall`;
  }
  if (plan.length > 1)
    return `Corner screen · two panels, ${plan[0].resW} and ${plan[1].resW} px wide · `
         + `${m2(wall.metres.w)} × ${m2(wall.metres.h)} m unfolded`;
  return `One screen · ${plan[0].resW} × ${plan[0].resH} px · ${m2(wall.metres.w)} × ${m2(wall.metres.h)} m`;
}

/**
 * The page that ships beside the clips.
 *
 * @param {Array}  plan     panelPlan() — each entry's key, label, resolution
 *                          and, for a wall, its row and column
 * @param {(p:object)=>string} nameOf  the exact filename each panel was written
 *        under, passed in rather than rebuilt here so the hrefs cannot drift
 *        from the entries actually in the ZIP
 * @param {{fps:number, seconds:number}} clip
 * @param {object} [custom]  a layout supplied by the caller instead of the builder's own screen
 *        settings — the Unwrap tool's, whose screens have a size in pixels and no scene behind
 *        them. { cells:[{left,top,width,height}] (percent, one per plan entry), aspect,
 *        summary, sizeLine, note, title }. Everything else on the page is the same.
 * @returns {Promise<string>}
 */
export async function contactSheetHTML(plan, nameOf, clip = {}, custom = null) {
  const logo = await logoDataUrl();
  const title = String(custom ? (custom.title || '') : (projectSlug() || '')).replace(/_/g, ' ');
  const wall = custom ? { aspect: custom.aspect, cells: custom.cells, metres: { w: 0, h: 0 } } : wallCells(plan);

  /* Playing, muted, looping — a wall that is doing nothing says less than a
     still would. autoplay is honoured because they are muted; a browser that
     refuses it anyway leaves the controls, which is why they are there. */
  const live = plan.map((p, i) => {
    const c = wall.cells[i];
    return `      <video class="screen" src="${esc(nameOf(p))}" autoplay loop muted playsinline
        style="left:${c.left.toFixed(4)}%;top:${c.top.toFixed(4)}%;width:${c.width.toFixed(4)}%;height:${c.height.toFixed(4)}%"
        title="${esc(p.key)} — ${esc(p.label)}"></video>`;
  }).join('\n');

  const cards = plan.map(p => {
    const file = nameOf(p);
    return `      <figure class="cell">
        <video src="${esc(file)}" autoplay loop muted playsinline></video>
        <figcaption>
          <b>${esc(p.key)}</b>
          <span>${esc(p.label)}</span>
          <code>${p.resW} × ${p.resH}</code>
          <a href="${esc(file)}" download>${esc(file)}</a>
        </figcaption>
      </figure>`;
  }).join('\n');

  const len = clip.seconds ? ` · ${clip.seconds.toFixed(2)} s at ${clip.fps} fps` : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Illusio 3D${title ? ' — ' + esc(title) : ''}</title>
<style>
  :root{
    --page:#141618; --panel:#2C2F33; --line:#545A62;
    --text:#E4E6E8; --muted:#C6CDD4; --dim:#A2A9AF; --accent:#71A8FF;
    --sans:system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
    --mono:ui-monospace,'SF Mono',Menlo,Consolas,monospace;
  }
  *{box-sizing:border-box}
  body{margin:0;background:var(--page);color:var(--text);font-family:var(--sans);line-height:1.5}
  .wrap{max-width:1100px;margin:0 auto;padding:28px 20px 64px}
  header{display:flex;align-items:center;gap:14px;padding-bottom:18px;border-bottom:1px solid var(--line)}
  header img{width:40px;height:40px;display:block;flex:0 0 auto}
  header h1{margin:0;font-size:19px;font-weight:600;letter-spacing:-.01em}
  header p{margin:2px 0 0;font-size:12.5px;color:var(--dim)}
  /* THE WALL. One box in the installation's own aspect, each screen absolutely
     placed in it as a percentage — so the bezel gaps stay proportional at any
     width, and the piece can be read across the seams the way it will hang. */
  /* The ground shows THROUGH the bezel gaps, so the wall reads as screens with
     spaces between them rather than as one rectangle. */
  .wall{position:relative;margin:22px 0 6px;background:var(--panel);border:1px solid var(--line);
        border-radius:6px;overflow:hidden}
  /* Each screen outlines itself. Invisible once a clip is playing over it, and
     the difference between "the files have not loaded" and "this page is
     broken" when one is not — a missing file, or a machine that will not
     decode it, would otherwise leave a black box saying nothing. */
  .screen{position:absolute;display:block;object-fit:cover;background:#000;
          box-shadow:inset 0 0 0 1px rgba(255,255,255,.10)}
  .wallnote{margin:0 0 26px;font-size:11.5px;color:var(--dim);font-family:var(--mono)}
  h2{font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--dim);margin:0 0 10px}
  /* TWO COLUMNS, as asked — dropping to one only where two would be narrower
     than a phone can show a frame in. */
  .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px}
  @media (max-width:720px){ .grid{grid-template-columns:1fr} }
  .cell{margin:0;background:var(--panel);border:1px solid var(--line);border-radius:10px;overflow:hidden}
  .cell video{display:block;width:100%;background:#000;aspect-ratio:16/9;object-fit:contain}
  figcaption{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding:9px 11px 11px}
  figcaption b{font-size:13px}
  figcaption span{font-size:12px;color:var(--muted)}
  figcaption code{font-family:var(--mono);font-size:11px;color:var(--dim)}
  figcaption a{margin-left:auto;font-family:var(--mono);font-size:10.5px;color:var(--accent);
               text-decoration:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%}
  figcaption a:hover{text-decoration:underline}
  .note{margin:22px 0 0;font-size:12.5px;color:var(--dim);max-width:70ch}
  footer{margin-top:26px;padding-top:14px;border-top:1px solid var(--line);font-size:12px;color:var(--dim)}
</style>
</head>
<body>
<div class="wrap">
  <header>
    ${logo ? '<img src="' + logo + '" alt="">' : ''}
    <div>
      <h1>Illusio 3D${title ? ' — ' + esc(title) : ''}</h1>
      <p>${esc(custom ? custom.summary : summary(plan, wall))}${esc(len)}</p>
    </div>
  </header>

  <div class="wall" style="aspect-ratio:${wall.aspect.toFixed(6)}">
${live}
  </div>
  <p class="wallnote">${esc(custom ? custom.sizeLine : m2(wall.metres.w) + ' × ' + m2(wall.metres.h) + ' m · playing in position')}</p>

  <h2>The files in this folder</h2>
  <div class="grid">
${cards}
  </div>

  <p class="note">${custom ? esc(custom.note) : NOTE}</p>

  <footer>Made with Illusio 3D &middot; ${SITE}</footer>
</div>
</body>
</html>
`;
}
